import { useCallback, useState } from 'react';

/** Mirrors the API limit (MAX_TOPIC_CHARS in study-plan.constants.ts). */
export const MAX_TOPIC_CHARS = 120;

/**
 * The request fragment for a question's topic. Nothing until the author touches
 * the field, so a question that never had a topic (or whose topic was set by
 * the tagger) is saved without one. A cleared field sends '' because that is
 * what removes the topic on the server.
 */
export function topicPatch(value: string, touched: boolean): { topic?: string } {
  return touched ? { topic: value.trim() } : {};
}

export interface QuestionTopicField {
  value: string;
  onChange: (next: string) => void;
  /** Re-seed from a loaded question, or clear for "save & add another". */
  reset: (next?: string) => void;
  /** Spread into the save payload. */
  patch: { topic?: string };
}

export function useQuestionTopicField(initial: string | undefined): QuestionTopicField {
  const [value, setValue] = useState(initial ?? '');
  const [touched, setTouched] = useState(false);
  const onChange = useCallback((next: string) => {
    setValue(next);
    setTouched(true);
  }, []);
  const reset = useCallback((next?: string) => {
    setValue(next ?? '');
    setTouched(false);
  }, []);
  return { value, onChange, reset, patch: topicPatch(value, touched) };
}
